import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

export interface LinkPreviewData {
  title: string;
  description: string | null;
  image: string | null;
  url: string;
  siteName?: string;
  // Rich embed support
  embedHtml?: string | null;
  embedType?: 'player' | 'app' | 'rich' | null;
  aspectRatio?: number | null;
  embedHeight?: number | null;
  isEmbeddable?: boolean;
  provider?: string;
}

const LINK_PREVIEW_CACHE_TTL_MS = 5 * 60 * 1000;
const previewCache = new Map<string, { data: LinkPreviewData; expiresAt: number }>();
const inFlightRequests = new Map<string, Promise<LinkPreviewData>>();
let iframelyDisabledUntil = 0;
let callableDisabledUntil = 0;

function shouldUseCallableLinkPreview(): boolean {
  // Temporarily disabled in production until callable CORS path is stable.
  return false;
}

function isIframelyTemporarilyDisabled(): boolean {
  try {
    const stored = typeof sessionStorage !== 'undefined' ? Number(sessionStorage.getItem('iframely_disabled_until') || '0') : 0;
    if (Date.now() < Math.max(iframelyDisabledUntil, stored)) {
      return true;
    }
  } catch {
    // ignore
  }
  return Date.now() < iframelyDisabledUntil;
}

function disableIframelyTemporarily(durationMs = 60 * 60 * 1000) {
  iframelyDisabledUntil = Date.now() + durationMs;
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('iframely_disabled_until', String(iframelyDisabledUntil));
    }
  } catch {
    // ignore
  }
}

function isCallableTemporarilyDisabled(): boolean {
  return Date.now() < callableDisabledUntil;
}

function normalizeUrlForCache(url: string): string {
  return url.trim();
}

function getCachedPreview(url: string): LinkPreviewData | null {
  const cached = previewCache.get(url);
  if (!cached) {
    return null;
  }

  if (cached.expiresAt <= Date.now()) {
    previewCache.delete(url);
    return null;
  }

  return cached.data;
}

function setCachedPreview(url: string, data: LinkPreviewData): void {
  previewCache.set(url, {
    data,
    expiresAt: Date.now() + LINK_PREVIEW_CACHE_TTL_MS,
  });
}

async function fetchViaCallable(url: string): Promise<LinkPreviewData> {
  const configuredRegions = (import.meta.env.VITE_LINK_PREVIEW_FUNCTION_REGIONS as string | undefined)
    ?.split(',')
    .map((region) => region.trim())
    .filter(Boolean);
  const regions = configuredRegions && configuredRegions.length > 0 ? configuredRegions : ['europe-west1'];
  let lastError: unknown = null;

  for (const region of regions) {
    try {
      const functions = getFunctions(getApp(), region);
      const getLinkPreviewFn = httpsCallable(functions, 'getLinkPreview');
      const result = await getLinkPreviewFn({ url });
      return result.data as LinkPreviewData;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Callable link preview failed in all regions');
}

function extractMediaUrl(value: unknown): string | null {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }

  if (value && typeof value === 'object' && typeof (value as { url?: unknown }).url === 'string') {
    const url = (value as { url: string }).url.trim();
    return url.length > 0 ? url : null;
  }

  return null;
}

// Use Firebase Cloud Function to avoid CORS issues
export async function getLinkPreview(url: string): Promise<LinkPreviewData> {
  const normalizedUrl = normalizeUrlForCache(url);
  if (!normalizedUrl) {
    return createFallbackPreview(url);
  }

  const cached = getCachedPreview(normalizedUrl);
  if (cached) {
    return cached;
  }

  // Internal InTouch URLs: provide first-party preview immediately without third-party API
  if (normalizedUrl.includes('intouchsn.com') || normalizedUrl.includes('localhost')) {
    const fallback = createFallbackPreview(normalizedUrl);
    fallback.title = 'InTouch Social & Web3 Network';
    fallback.description = 'Connect, mine INT, stream music, browse marketplace, and share moments on InTouch.';
    fallback.siteName = 'InTouch';
    setCachedPreview(normalizedUrl, fallback);
    return fallback;
  }

  const existingRequest = inFlightRequests.get(normalizedUrl);
  if (existingRequest) {
    return existingRequest;
  }

  const requestPromise = (async () => {
  try {
    if (shouldUseCallableLinkPreview() && !isCallableTemporarilyDisabled()) {
      const data = await fetchViaCallable(normalizedUrl);
      setCachedPreview(normalizedUrl, data);
      return data;
    }
  } catch (error) {
    callableDisabledUntil = Date.now() + 10 * 60 * 1000;
    if (import.meta.env.DEV) {
      console.warn('[getLinkPreview] Callable failed, falling back to direct API.', error);
    }
  }

    const allowDirectBrowserFallback = import.meta.env.VITE_LINK_PREVIEW_DIRECT_FALLBACK !== 'false';
    if (!allowDirectBrowserFallback) {
      const fallback = createFallbackPreview(normalizedUrl);
      setCachedPreview(normalizedUrl, fallback);
      return fallback;
    }

    const fallbackData = await getLinkPreviewDirect(normalizedUrl);
    setCachedPreview(normalizedUrl, fallbackData);
    return fallbackData;
  })().finally(() => {
    inFlightRequests.delete(normalizedUrl);
  });

  inFlightRequests.set(normalizedUrl, requestPromise);
  return requestPromise;
}

// Direct hybrid API call (fallback)
// - Iframely first for rich social embeds
// - Microlink enrichment for better article metadata/image
async function getLinkPreviewDirect(url: string): Promise<LinkPreviewData> {
  try {
    const iframelyApiKey = import.meta.env.VITE_IFRAMELY_API_KEY as string | undefined;
    let iframelyData: any = null;

    if (iframelyApiKey && !isIframelyTemporarilyDisabled()) {
      const iframelyUrl = `https://cdn.iframe.ly/api/iframely?url=${encodeURIComponent(url)}&api_key=${iframelyApiKey}&iframe=1`;

      const iframelyResponse = await fetch(iframelyUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      });

      if (iframelyResponse.ok) {
        iframelyData = await iframelyResponse.json();
      } else {
        if (iframelyResponse.status === 401 || iframelyResponse.status === 403) {
          disableIframelyTemporarily(60 * 60 * 1000);
          if (import.meta.env.DEV) {
            console.warn('[getLinkPreviewDirect] Disabling Iframely for 1 hour due to auth/rate response:', iframelyResponse.status);
          }
        }
      }
    }

    const iframelyTitle = iframelyData?.meta?.title || iframelyData?.meta?.og?.title || iframelyData?.meta?.twitter?.title || '';
    const iframelyDescription = iframelyData?.meta?.description || iframelyData?.meta?.og?.description || iframelyData?.meta?.twitter?.description || null;
    const iframelySiteName = iframelyData?.meta?.site || new URL(url).hostname.replace('www.', '');

    let iframelyImage = null;
    if (iframelyData?.links?.thumbnail && Array.isArray(iframelyData.links.thumbnail) && iframelyData.links.thumbnail.length > 0) {
      const thumbnails = iframelyData.links.thumbnail.sort((a: any, b: any) => {
        const aSize = (a.media?.width || 0) * (a.media?.height || 0);
        const bSize = (b.media?.width || 0) * (b.media?.height || 0);
        return bSize - aSize;
      });
      iframelyImage = thumbnails[0]?.href || null;
    }
    if (!iframelyImage) {
      iframelyImage = iframelyData?.meta?.og?.image || iframelyData?.meta?.twitter?.image || null;
    }
    if (!iframelyImage && iframelyData?.links?.icon && Array.isArray(iframelyData.links.icon) && iframelyData.links.icon.length > 0) {
      iframelyImage = iframelyData.links.icon[0]?.href || null;
    }

    let embedHtml: string | null = null;
    let embedType: 'player' | 'app' | 'rich' | null = null;
    let aspectRatio: number | null = null;
    let embedHeight: number | null = null;

    if (iframelyData?.links?.player && Array.isArray(iframelyData.links.player) && iframelyData.links.player.length > 0) {
      const player = iframelyData.links.player[0];
      if (player.html) {
        embedHtml = player.html;
        embedType = 'player';
        embedHeight = player.media?.height || null;
        aspectRatio = player.media?.['aspect-ratio'] || null;
      } else if (player.href) {
        const height = player.media?.height || 352;
        embedHtml = `<iframe src="${player.href}" style="border: 0; width: 100%; height: ${height}px;" allow="encrypted-media; clipboard-write; picture-in-picture; fullscreen"></iframe>`;
        embedType = 'player';
        embedHeight = height;
      }
    }

    if (!embedHtml && iframelyData?.links?.app && Array.isArray(iframelyData.links.app) && iframelyData.links.app.length > 0) {
      const app = iframelyData.links.app[0];
      if (app.html) {
        embedHtml = app.html;
        embedType = 'app';
        aspectRatio = app.media?.['aspect-ratio'] || null;
        embedHeight = app.media?.height || null;
      }
    }

    if (!embedHtml && iframelyData?.html) {
      embedHtml = iframelyData.html;
      embedType = 'rich';
    }

    const microlinkUrl = `https://api.microlink.io/?url=${encodeURIComponent(url)}`;

    let microlinkJson: any = null;
    try {
      const microlinkResponse = await fetch(microlinkUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      });

      if (microlinkResponse.status === 429 || microlinkResponse.status === 425) {
        if (import.meta.env.DEV) {
          console.warn('[getLinkPreviewDirect] Microlink rate limit reached');
        }
      }

      if (microlinkResponse.ok) {
        microlinkJson = await microlinkResponse.json();
      }
    } catch (microlinkError) {
      if (import.meta.env.DEV) {
        console.warn('[getLinkPreviewDirect] Microlink request failed, keeping Iframely result:', microlinkError);
      }
    }
    const payload = (microlinkJson?.data && typeof microlinkJson.data === 'object') ? microlinkJson.data : {};

    const microlinkTitle = payload.title || '';
    const microlinkDescription = payload.description || null;
    const microlinkImage = extractMediaUrl(payload.image) || extractMediaUrl(payload.logo);
    const resolvedUrl = payload.url || iframelyData?.url || url;
    const microlinkSiteName = payload.publisher || new URL(url).hostname.replace('www.', '');

    const isEmbeddable = !!embedHtml;

    const previewData: LinkPreviewData = {
      title: isEmbeddable ? (iframelyTitle || microlinkTitle || url) : (microlinkTitle || iframelyTitle || url),
      description: isEmbeddable ? (iframelyDescription || microlinkDescription) : (microlinkDescription || iframelyDescription),
      image: isEmbeddable ? (iframelyImage || microlinkImage) : (microlinkImage || iframelyImage),
      url: resolvedUrl,
      siteName: iframelySiteName || microlinkSiteName,
      embedHtml,
      embedType,
      aspectRatio,
      embedHeight,
      isEmbeddable,
      provider: isEmbeddable ? (iframelySiteName || 'iframely').toLowerCase() : (microlinkJson ? 'microlink' : 'iframely')
    };
    
    return previewData;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[getLinkPreviewDirect] Error, using fallback preview:', error);
    }
    return createFallbackPreview(url);
  }
}

function createFallbackPreview(url: string): LinkPreviewData {
  try {
    const fallbackData = {
      title: new URL(url).hostname.replace('www.', ''),
      description: null,
      image: null,
      url: url,
      siteName: new URL(url).hostname.replace('www.', '').toUpperCase()
    };
    return fallbackData;
  } catch {
    return {
      title: url,
      description: null,
      image: null,
      url: url,
      siteName: 'Link'
    };
  }
}

export async function getLinksPreview(content: string): Promise<LinkPreviewData[]> {
  const urls = extractUrls(content);
  if (urls.length === 0) return [];

  try {
    const previews = await Promise.all(
      urls.map(url => getLinkPreview(url).catch(() => null))
    );

    return previews.filter((preview): preview is LinkPreviewData => preview !== null);
  } catch (error) {
    console.error('Error fetching link previews:', error);
    return [];
  }
}

function extractUrls(text: string): string[] {
  if (!text) return [];
  
  const urlRegex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/gi;
  const matches = text.match(urlRegex);
  
  return matches ? [...new Set(matches)] : [];
}